import useSWRInfinite from 'swr/infinite';

import {
  fetchTimeline,
  type TransactionLine,
  type TimelinePage,
  type TimelineQuery,
} from '../data/timelineRepository.js';

const KEY_PREFIX = 'timeline:';

export type TimelineState =
  | { status: 'loading' }
  | { status: 'error'; message: string; retry: () => void }
  | {
      status: 'ready';
      items: readonly TransactionLine[];
      hasMore: boolean;
      isLoadingMore: boolean;
      loadMore: () => void;
      refresh: () => Promise<unknown>;
    };

/**
 * Pages the timeline with a keyset cursor: each page asks for rows strictly older than the
 * last one it received, so a new entry posted while reading can neither duplicate nor hide a
 * row (an OFFSET would do both).
 *
 * The cursor travels inside the cache key, which keeps every page individually cacheable and
 * lets `mutate` find them all by prefix.
 */
export function useTimeline(query: Omit<TimelineQuery, 'cursor'>): TimelineState {
  const { data, error, isLoading, isValidating, size, setSize, mutate } = useSWRInfinite<
    TimelinePage,
    Error
  >(
    (index, previous: TimelinePage | null) => {
      if (index > 0 && (previous === null || previous.nextCursor === null)) return null;
      const cursor = index === 0 ? undefined : previous?.nextCursor;
      return `${KEY_PREFIX}${JSON.stringify({ ...query, ...(cursor ? { cursor } : {}) })}`;
    },
    (key: string) => fetchTimeline(JSON.parse(key.slice(KEY_PREFIX.length)) as TimelineQuery),
    { revalidateOnFocus: false, revalidateFirstPage: false, shouldRetryOnError: false },
  );

  if (error) {
    return {
      status: 'error',
      message: error.message || 'Không tải được danh sách giao dịch.',
      retry: () => {
        void mutate();
      },
    };
  }

  if (isLoading || data === undefined) return { status: 'loading' };

  const pages = data;
  const lastPage = pages.at(-1);

  return {
    status: 'ready',
    items: pages.flatMap((page) => page.items),
    hasMore: lastPage?.nextCursor != null,
    isLoadingMore: isValidating && pages.length < size,
    loadMore: () => {
      void setSize(size + 1);
    },
    refresh: () => mutate(),
  };
}
